import React, { useContext } from 'react';
import { ThemeContext } from '../context/ThemeContext';
import socialData from '../data/javascripts/socialData';
import { Link, useLocation, useNavigate } from 'react-router-dom';

const Footer = () => {
  const { darkMode } = useContext(ThemeContext);
  const currentYear = new Date().getFullYear();
  const navigate = useNavigate();
  const location = useLocation();

  // Define navigation items.
  // NOTE: Items linking to sections on the homepage still use path: '/' and require the section 'id'.
  const navItems = [
    { label: 'Home', path: '/', id: 'home' },
    { label: 'Articles', path: '/', id: 'articles' },
    { label: 'About', path: '/', id: 'about' },
    // ⭐ Assuming /gallery, /projects, /journey, /contact should now go to separate pages for professional structure
    { label: 'Gallery', path: '/gallery', id: 'gallery' }, 
    { label: 'Projects', path: '/projects', id: 'projects' }, 
    { label: 'Journey', path: '/journey', id: 'journey' }, 
    { label: 'Contact', path: '/contact', id: 'contact' }, 
  ];

  const handleNavClick = (e, sectionId, path) => {
    // If it's a dedicated page, use Link
    if (path !== '/') {
        // Prevent default only to ensure smooth navigation is handled by React Router
        // navigate(path); // This line is not needed if rendering a standard Link component below
        return; 
    }

    // Handle smooth scroll for homepage sections
    e.preventDefault();

    if (location.pathname === '/') {
      const section = document.getElementById(sectionId);
      if (section) {
        window.scrollTo({
          top: section.offsetTop,
          behavior: 'smooth',
        });
      }
    } else {
      // Navigate to homepage and scroll after load
      navigate('/', { state: { scrollTo: sectionId } });
    }
  };

  // --- Tailwind Theme Classes ---
  const footerClasses = darkMode 
    ? ' bg-card-light dark:bg-card-dark text-gray-300' 
    : ' bg-card-light dark:bg-card-dark text-gray-700';
  
  const headingClasses = darkMode ? 'text-white' : 'text-gray-900';
  const mutedTextClasses = darkMode ? 'text-gray-400' : 'text-gray-500';
  const linkClasses = `hover:text-primary dark:hover:text-secondary transition duration-200`;
  const socialIconClasses = `w-8 h-8 flex items-center justify-center rounded-full text-xl 
                             ${darkMode ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'} 
                             transition duration-300 me-4`;

  return (
    <footer className={`py-12 ${footerClasses}`}>
      <div className="container mx-auto px-4">
        {/* --- Main Content Grid --- */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mb-10">
          
          {/* 1. Bio / Branding */}
          <div className="md:col-span-1">
            <div className="mb-4">
              <h3 className={`text-2xl font-bold mb-3 ${headingClasses}`}>
                Varun Rao
              </h3>
              <p className={`text-lg ${mutedTextClasses} mb-4`}>
                AI Engineer & GPU Expert
              </p>
            </div>
            
            {/* Social Icons */}
            <div className="flex justify-start flex-wrap">
              {socialData.map((item, index) => (
                <a
                  key={index}
                  href={item.url}
                  className={socialIconClasses}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={item.name}
                >
                  {/* Assuming item.icon contains the full bi- class name, e.g., 'linkedin' for 'bi bi-linkedin' */}
                  <i className={`bi bi-${item.icon}`}></i>
                </a>
              ))}
            </div>
          </div>

          {/* 2. Quick Links */}
          <div className="md:col-span-1">
            <h5 className={`text-lg font-semibold mb-4 ${headingClasses}`}>Quick Links</h5>
            <ul className="list-none space-y-2 p-0">
              {navItems.map((item) => (
                <li key={item.label}>
                  <Link
                    to={item.path}
                    onClick={(e) => handleNavClick(e, item.id, item.path)}
                    className={`text-base ${linkClasses} ${darkMode ? 'text-gray-400' : 'text-gray-700'}`}
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          
          {/* 3. Empty Column for Layout (Optional, but maintains the 3-column spacing) */}
          <div className="md:col-span-1 hidden md:block">
            {/* Use for potential future content like contact info or map */}
          </div>
        </div>

        {/* --- Divider --- */}
        <div className={`w-full h-px my-8 ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}></div>

        {/* --- Copyright --- */}
        <div className="text-center md:text-start">
          <p className="text-xs tracking-widest font-light uppercase">
            © {currentYear} 
            <span className={`ml-1 ${darkMode ? 'text-gray-200' : 'text-gray-800'} font-normal`}>Varun Rao</span>. 
            All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;